import React, { useEffect, useRef, useState } from "react";
import { AlertCircle, Check } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Click-to-edit cell used across the LOS canvas. Text, number, select or multiline – commits on blur/Enter, Escape cancels.
 * `display` is what the resting cell shows; `value` is what gets edited. Saving is visible: lagrer → lagret, or feilet
 * (the attempted value stays in the cell, marked, and a click re-opens it for retry) – never a silent revert.
 */
export function EditCell({ value, display, options, onSave, testId, align = "left", placeholder = "–", multiline = false, numeric = false, className, disabled = false, allowEmpty = false, title, autoEdit = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [state, setState] = useState(null); // null | "saving" | "saved" | "error"
  const [pending, setPending] = useState(null);
  const taRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    if (autoEdit && !disabled) {
      setDraft(value === null || value === undefined ? "" : String(value));
      setEditing(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoEdit]);

  useEffect(() => () => clearTimeout(timer.current), []);

  useEffect(() => {
    if (!editing || !multiline || !taRef.current) return;
    const ta = taRef.current;
    ta.style.height = "0px";
    ta.style.height = `${Math.min(Math.max(ta.scrollHeight, 60), 320)}px`;
  }, [editing, multiline, draft]);

  const start = () => {
    if (disabled || state === "saving") return;
    setDraft(state === "error" ? String(pending ?? "") : value === null || value === undefined ? "" : String(value));
    setEditing(true);
  };
  const commit = async (v) => {
    setEditing(false);
    const val = v ?? draft;
    const same = String(val ?? "") === String(value ?? "");
    if (same && state !== "error") return;
    if (val === "" && !allowEmpty) return;
    const out = val === "" ? null : val;
    setPending(out);
    setState("saving");
    try {
      await onSave(out);
      setState("saved");
      setPending(null);
      timer.current = setTimeout(() => setState(null), 1400);
    } catch {
      setState("error");
    }
  };

  if (options) {
    const errored = state === "error";
    const cur = errored ? pending : value;
    const statusTitle = state === "saving" ? "Lagrer…" : state === "saved" ? "Lagret" : errored ? "Ikke lagret – velg igjen for å prøve på nytt" : null;
    return (
      <span className="relative block" data-save-state={state || undefined}>
        <select value={cur === null || cur === undefined ? "" : String(cur)} disabled={disabled || state === "saving"} onChange={(e) => commit(e.target.value)} title={statusTitle || title || undefined} aria-busy={state === "saving" || undefined}
          className={cn("editable h-7 w-full min-w-[96px] cursor-pointer appearance-none truncate rounded-[4px] border border-transparent bg-transparent py-0 pl-1 pr-5 text-[13px] text-foreground/85 outline-none hover:bg-[hsl(210_20%_97%)] focus-visible:border-primary/40 focus-visible:bg-white focus-visible:ring-2 focus-visible:ring-primary/15 disabled:cursor-default", (cur === null || cur === undefined || cur === "") && "text-muted-foreground/70", errored && "bg-[hsl(var(--v-blocked))]/[0.08] text-[hsl(var(--v-blocked))]", align === "right" && "text-right", className)}
          data-testid={testId} data-save-state={state || undefined}>
          <option value="">{placeholder}</option>
          {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <svg aria-hidden className="pointer-events-none absolute right-1 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground/60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m6 9 6 6 6-6" /></svg>
        {state ? (
          <span className={cn("pointer-events-none absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full text-white shadow-sm", state === "saving" && "bg-[hsl(var(--brand))]/70", state === "saved" && "bg-[hsl(var(--v-ok))] save-pop", errored && "bg-[hsl(var(--v-blocked))]")} aria-label={statusTitle} data-testid={testId ? `${testId}-status` : undefined} data-state={state}>
            {state === "saving" ? <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> : state === "saved" ? <Check className="h-2.5 w-2.5" strokeWidth={3} /> : <AlertCircle className="h-2.5 w-2.5" strokeWidth={3} />}
          </span>
        ) : null}
      </span>
    );
  }
  if (editing) {
    if (multiline) {
      return (
        <textarea
          ref={taRef}
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => commit()}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) commit();
            if (e.key === "Escape") setEditing(false);
          }}
          className={cn("block w-full resize-none rounded-md border border-primary/40 bg-white px-2 py-1.5 text-[13px] leading-[1.55] outline-none ring-2 ring-primary/15", className)}
          data-testid={testId ? `${testId}-input` : undefined}
        />
      );
    }
    return (
      <input
        autoFocus
        value={draft}
        inputMode={numeric ? "decimal" : undefined}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => commit()}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") setEditing(false);
        }}
        className={cn("h-7 w-full min-w-[64px] rounded-md border border-primary/40 bg-white px-1.5 text-[12.5px] outline-none ring-2 ring-primary/15", numeric && "font-mono text-[12px]", align === "right" && "text-right", className)}
        data-testid={testId ? `${testId}-input` : undefined}
      />
    );
  }
  const errored = state === "error";
  const shownValue = errored ? pending : value;
  const missing = shownValue === null || shownValue === undefined || shownValue === "";
  const statusTitle = state === "saving" ? "Lagrer…" : state === "saved" ? "Lagret" : errored ? "Ikke lagret – klikk for å prøve igjen" : null;
  return (
    <button type="button" onClick={start} disabled={disabled} aria-busy={state === "saving" || undefined} title={statusTitle || title || (disabled ? undefined : "Klikk for å redigere")} className={cn("editable relative -mx-1 block w-[calc(100%+8px)] px-1 py-0.5 text-left disabled:cursor-default", align === "right" && "text-right", multiline && "whitespace-pre-wrap", missing && "text-muted-foreground", state === "saving" && "opacity-60", errored && "rounded-sm bg-[hsl(var(--v-blocked))]/[0.08] text-[hsl(var(--v-blocked))] underline decoration-dotted decoration-[hsl(var(--v-blocked))]/70 underline-offset-2", className)} data-testid={testId} data-save-state={state || undefined} data-numeric={numeric && !disabled ? "1" : undefined}>
      {missing ? <span className="text-muted-foreground/60">{placeholder}</span> : errored ? String(pending) : (display ?? value)}
      {state ? (
        <span className={cn("pointer-events-none absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full text-white shadow-sm", state === "saving" && "bg-[hsl(var(--brand))]/70", state === "saved" && "bg-[hsl(var(--v-ok))] save-pop", errored && "bg-[hsl(var(--v-blocked))]")} aria-label={statusTitle} data-testid={testId ? `${testId}-status` : undefined} data-state={state}>
          {state === "saving" ? <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> : state === "saved" ? <Check className="h-2.5 w-2.5" strokeWidth={3} /> : <AlertCircle className="h-2.5 w-2.5" strokeWidth={3} />}
        </span>
      ) : null}
    </button>
  );
}
